import { useTranslation } from 'i18n'
import React from 'react'
import Modal from 'react-modal'
import { SStyled, useSTheme } from 'styles/theme'
import SBox from './SBox'
import SButton, { SButtonType } from './SButton'
import SText from './SText'
import SFlex from './SFlex'
import ConditionalRender from 'utils/conditionalRender'
import { useOnClickOutside } from 'utils/customHooks'
import XColors from 'v2/styles/colors'

interface Props {
    title: string | React.ReactNode
    message: string | React.ReactNode
    onConfirm?: () => void
    visible: boolean
    onCancel?: () => void
    onClickOutside?: () => void
    children?: React.ReactNode
    confirmMessage?: string
    modalRef?: React.RefObject<HTMLInputElement>
    loading?: boolean
    disableButtons?: boolean
    wide?: true
    isLongBeachV2Design?: boolean
}

const ContentWrapper = SStyled(SBox)`
    width: 500px;
    @media screen and (max-width: ${(p) => p.theme.breakpoints[0]}) {
        width: 100%;
    }
`

const ContentWrapperWide = SStyled(SBox)`
    max-width: 80vw;
    max-height: 90vh;
    overflow: auto;
`

const SConfirmModal: React.FC<Props> = (props: Props) => {
    const {
        title,
        message,
        onCancel,
        onConfirm,
        onClickOutside,
        visible,
        children,
        confirmMessage,
        modalRef,
        disableButtons,
        loading,
        isLongBeachV2Design,
    } = props
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const ModalTitle = SStyled(SText)`
    font-size: 24px;
    font-weight: ${(p) => p.theme.fontWeights.bold};
    margin-bottom: ${(p) => p.theme.space[6]}px;
    text-align: ${isLongBeachV2Design ? 'center' : 'left'};
    color: ${isLongBeachV2Design ? `${XColors.lbBlue} !important` : 'black'};
    font-family: ${isLongBeachV2Design ? 'Raleway' : 'inherit'};
`

    const ModalMessage = SStyled(SText)`
    font-size: ${(p) => (isLongBeachV2Design ? p.theme.fontSizes[3] : p.theme.fontSizes[4])}px;
    font-family: ${isLongBeachV2Design ? theme.fonts.terms : 'inherit'};
    color: ${(p) => p.theme.colors.gray400};
    margin-bottom: ${(p) => p.theme.space[8]}px;
    white-space: pre-wrap;
`

    if (onClickOutside) {
        useOnClickOutside(modalRef, onClickOutside)
    }

    const ComponentWrapper = props.wide ? ContentWrapperWide : ContentWrapper

    if (visible) {
        return (
            <Modal isOpen={visible} overlayClassName={'remove-style'} className={'remove-style'}>
                <ConditionalRender if={!children}>
                    <ComponentWrapper px={[7, 9, 9]} py={[7, 8, 8]} ref={modalRef}>
                        <ModalTitle>{title}</ModalTitle>
                        <ModalMessage>{message}</ModalMessage>

                        <SFlex flexDirection="row">
                            <ConditionalRender if={onCancel}>
                                <SButton
                                    mr={5}
                                    loading={disableButtons}
                                    width={'100%'}
                                    buttonType={SButtonType.Cancel}
                                    onClick={onCancel}
                                    flex="1 1 0"
                                    ga4_key={t('cancel', { lang: 'en' })}
                                >
                                    {t('cancel')}
                                </SButton>
                            </ConditionalRender>
                            <ConditionalRender if={onConfirm}>
                                <SButton
                                    width={'100%'}
                                    onClick={onConfirm}
                                    flex="1 1 0"
                                    loading={loading}
                                    ga4_key={t('confirm', { lang: 'en' })}
                                >
                                    {confirmMessage ? confirmMessage : t('confirm')}
                                </SButton>
                            </ConditionalRender>
                        </SFlex>
                    </ComponentWrapper>
                </ConditionalRender>
                <ConditionalRender if={children}>
                    <ComponentWrapper px={[7, 9, 9]} py={[7, 8, 8]} ref={modalRef}>
                        <ModalTitle>{title}</ModalTitle>
                        {children}
                        <SFlex flexDirection="row">
                            <ConditionalRender if={onCancel}>
                                <SButton
                                    mr={5}
                                    loading={disableButtons}
                                    width={'100%'}
                                    buttonType={SButtonType.Cancel}
                                    onClick={onCancel}
                                    flex="1 1 0"
                                    ga4_key={t('cancel', { lang: 'en' })}
                                >
                                    {t('cancel')}
                                </SButton>
                            </ConditionalRender>
                            <ConditionalRender if={onConfirm}>
                                <SButton
                                    width={'100%'}
                                    onClick={onConfirm}
                                    flex="1 1 0"
                                    loading={loading}
                                    ga4_key={t('confirm', { lang: 'en' })}
                                >
                                    {confirmMessage ? confirmMessage : t('confirm')}
                                </SButton>
                            </ConditionalRender>
                        </SFlex>
                    </ComponentWrapper>
                </ConditionalRender>
            </Modal>
        )
    } else {
        return null
    }
}

export default SConfirmModal
